/**
 * Trovva — Link-in-Bio Settings
 *
 * Settings panel where agents configure their public bio page.
 * Manages: slug, headline, theme, accent color, and an ordered list of links.
 * Saves to users.bio_links (JSONB column).
 *
 * Rendered inside SettingsPage → Profile tab.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Link2, Plus, Trash2, GripVertical, Eye, Copy, Check,
  Calendar, Search, Phone, Mail, Home, Star, MapPin,
  Camera, Video, Users, ExternalLink,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

const ICON_OPTIONS = [
  { value: 'calendar', label: 'Calendar', Icon: Calendar },
  { value: 'search', label: 'Search', Icon: Search },
  { value: 'phone', label: 'Phone', Icon: Phone },
  { value: 'mail', label: 'Email', Icon: Mail },
  { value: 'home', label: 'Home', Icon: Home },
  { value: 'star', label: 'Star', Icon: Star },
  { value: 'map', label: 'Map', Icon: MapPin },
  { value: 'instagram', label: 'Instagram', Icon: Camera },
  { value: 'youtube', label: 'YouTube', Icon: Video },
  { value: 'facebook', label: 'Facebook', Icon: Users },
  { value: 'link', label: 'Link', Icon: Link2 },
];

const card = {
  background: theme.colors.card,
  borderRadius: theme.radius.lg,
  border: `1px solid ${theme.colors.border}`,
  padding: 24,
  marginBottom: 20,
};
const label = { display: 'block', fontSize: 13, fontWeight: 500, color: theme.colors.textLight, marginBottom: 4 };
const inputStyle = {
  width: '100%', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
  padding: '8px 12px', fontSize: 14, color: theme.colors.text, background: theme.colors.card,
  outline: 'none', boxSizing: 'border-box',
};
const btnPrimary = {
  padding: '8px 20px', background: theme.colors.primary, color: '#fff', border: 'none',
  borderRadius: theme.radius.sm, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

export default function LinkInBioSettings() {
  const [bioLinks, setBioLinks] = useState({ slug: '', headline: '', links: [], theme: 'light', accent_color: '#1B65A6' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data: { user } } = await supabase.auth.getUser(session.access_token);
      if (!user) return;
      // Fetch current bio_links
      const { data } = await supabase.from('users').select('bio_links, first_name, last_name').eq('id', user.id).maybeSingle();
      if (data?.bio_links) {
        setBioLinks(prev => ({ ...prev, ...data.bio_links }));
      } else if (data) {
        // Auto-generate slug from name
        const slug = [data.first_name, data.last_name].filter(Boolean).join('-').toLowerCase().replace(/[^a-z0-9-]/g, '');
        setBioLinks(prev => ({ ...prev, slug }));
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const { data: { user } } = await supabase.auth.getUser(session.access_token);
      if (!user) throw new Error('Not signed in');

      // Validate slug
      if (!bioLinks.slug || bioLinks.slug.length < 2) throw new Error('Slug must be at least 2 characters');
      const cleanSlug = bioLinks.slug.toLowerCase().replace(/[^a-z0-9-]/g, '');

      // Check slug uniqueness
      const { data: existing } = await supabase
        .from('users')
        .select('id')
        .eq('bio_links->>slug', cleanSlug)
        .neq('id', user.id)
        .maybeSingle();
      if (existing) throw new Error('This slug is already taken. Try a different one.');

      const { error } = await supabase.from('users').update({
        bio_links: { ...bioLinks, slug: cleanSlug },
      }).eq('id', user.id);

      if (error) throw error;
      setBioLinks(prev => ({ ...prev, slug: cleanSlug }));
      setMsg('Saved!');
    } catch (e) {
      setMsg(e.message);
    } finally { setSaving(false); }
  };

  const addLink = () => {
    setBioLinks(prev => ({
      ...prev,
      links: [...(prev.links || []), { label: '', url: '', icon: 'link' }],
    }));
  };

  const updateLink = (idx, field, value) => {
    setBioLinks(prev => ({
      ...prev,
      links: prev.links.map((l, i) => i === idx ? { ...l, [field]: value } : l),
    }));
  };

  const removeLink = (idx) => {
    setBioLinks(prev => ({ ...prev, links: prev.links.filter((_, i) => i !== idx) }));
  };

  const moveLink = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= (bioLinks.links || []).length) return;
    setBioLinks(prev => {
      const links = [...prev.links];
      [links[idx], links[newIdx]] = [links[newIdx], links[idx]];
      return { ...prev, links };
    });
  };

  const bioUrl = bioLinks.slug ? `${typeof window !== 'undefined' ? window.location.origin : ''}/api/bio?slug=${bioLinks.slug}` : '';

  const copyUrl = () => {
    if (bioUrl) {
      navigator.clipboard.writeText(bioUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Link2 size={20} style={{ color: theme.colors.primary }} />
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, margin: 0 }}>Link-in-Bio Page</h2>
      </div>
      <p style={{ fontSize: 13, color: theme.colors.textLight, marginBottom: 20, lineHeight: 1.5 }}>
        Create a public landing page for your social media bios, video descriptions, and email signatures.
        Share one link that connects people to everything you offer.
      </p>

      {/* Slug + Preview URL */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={label}>Your URL slug</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <span style={{
              padding: '8px 10px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`,
              borderRight: 'none', borderRadius: `${theme.radius.sm} 0 0 ${theme.radius.sm}`,
              fontSize: 13, color: theme.colors.textLight, whiteSpace: 'nowrap',
            }}>trovva.ai/bio/</span>
            <input
              value={bioLinks.slug || ''}
              onChange={e => setBioLinks(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder="jane-doe"
              style={{ ...inputStyle, borderRadius: `0 ${theme.radius.sm} ${theme.radius.sm} 0` }}
            />
          </div>
        </div>
        <div>
          <label style={label}>Headline</label>
          <input
            value={bioLinks.headline || ''}
            onChange={e => setBioLinks(prev => ({ ...prev, headline: e.target.value }))}
            placeholder="Your NJ Home Expert"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Theme + Accent */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={label}>Theme</label>
          <select
            value={bioLinks.theme || 'light'}
            onChange={e => setBioLinks(prev => ({ ...prev, theme: e.target.value }))}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </div>
        <div>
          <label style={label}>Accent color</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              value={bioLinks.accent_color || '#1B65A6'}
              onChange={e => setBioLinks(prev => ({ ...prev, accent_color: e.target.value }))}
              style={{ width: 40, height: 36, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, cursor: 'pointer', padding: 2 }}
            />
            <input
              value={bioLinks.accent_color || '#1B65A6'}
              onChange={e => setBioLinks(prev => ({ ...prev, accent_color: e.target.value }))}
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
        </div>
      </div>

      {/* Links */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ ...label, marginBottom: 10 }}>Links ({(bioLinks.links || []).length}/10)</label>
        {(bioLinks.links || []).map((link, idx) => (
          <div key={idx} style={{
            display: 'grid', gridTemplateColumns: '32px 100px 1fr 1fr 32px', gap: 8, alignItems: 'center',
            marginBottom: 8, padding: '8px 0', borderBottom: idx < (bioLinks.links || []).length - 1 ? `1px solid ${theme.colors.border}` : 'none',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button onClick={() => moveLink(idx, -1)} disabled={idx === 0}
                style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', padding: 0, fontSize: 10, color: theme.colors.textLight, opacity: idx === 0 ? 0.3 : 1 }}>▲</button>
              <button onClick={() => moveLink(idx, 1)} disabled={idx === (bioLinks.links || []).length - 1}
                style={{ background: 'none', border: 'none', cursor: idx === (bioLinks.links || []).length - 1 ? 'default' : 'pointer', padding: 0, fontSize: 10, color: theme.colors.textLight, opacity: idx === (bioLinks.links || []).length - 1 ? 0.3 : 1 }}>▼</button>
            </div>
            <select value={link.icon || 'link'} onChange={e => updateLink(idx, 'icon', e.target.value)}
              style={{ ...inputStyle, fontSize: 13, padding: '6px 8px' }}>
              {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input value={link.label || ''} onChange={e => updateLink(idx, 'label', e.target.value)}
              placeholder="Button label" style={{ ...inputStyle, fontSize: 13, padding: '6px 10px' }} />
            <input value={link.url || ''} onChange={e => updateLink(idx, 'url', e.target.value)}
              placeholder="https://..." style={{ ...inputStyle, fontSize: 13, padding: '6px 10px' }} />
            <button onClick={() => removeLink(idx)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: theme.colors.danger }}>
              <Trash2 size={15} />
            </button>
          </div>
        ))}

        {(bioLinks.links || []).length < 10 && (
          <button onClick={addLink} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', marginTop: 8,
            background: 'none', border: `1px dashed ${theme.colors.border}`, borderRadius: theme.radius.sm,
            color: theme.colors.primary, fontSize: 13, fontWeight: 500, cursor: 'pointer', width: '100%', justifyContent: 'center',
          }}>
            <Plus size={15} /> Add a link
          </button>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
          {saving ? 'Saving...' : 'Save Bio Page'}
        </button>

        {bioLinks.slug && (
          <>
            <button onClick={copyUrl} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              background: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
              color: theme.colors.text, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>

            <a href={bioUrl} target="_blank" rel="noopener noreferrer" style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              background: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
              color: theme.colors.text, fontSize: 13, fontWeight: 500, cursor: 'pointer', textDecoration: 'none',
            }}>
              <Eye size={14} /> Preview
            </a>
          </>
        )}

        {msg && (
          <span style={{ fontSize: 13, color: msg === 'Saved!' ? theme.colors.success : theme.colors.danger }}>{msg}</span>
        )}
      </div>
    </div>
  );
}
